import { useState } from 'react';
import { Link2, MessageCircle, Mail, Check, RefreshCw } from 'lucide-react';
import { Button, Input, useToast } from '@afrilink/ui';
import {
  buildWhatsAppMessage,
  buildEmailBlock,
  type PayLinkMessageInput,
} from '@/shared/lib/payLinkMessage';

/**
 * CopyPayLink — copy a payment link, or a paste-ready message wrapped around
 * it, to the clipboard.
 *
 * Used by two tiers with deliberately different scope (owner decision
 * 2026-07-27):
 *   - admin/support, on any open document (CustomerBillingTab, AdminInvoicesPage)
 *   - sales agents + managers, on QUOTATIONS ONLY (sales surfaces)
 * This component does not enforce that split. Scope is enforced where it
 * belongs: by RLS for admin/support, and server-side in the
 * sales-quotation-pay-link EF for the sales tier. Hiding a button is a UI
 * preference, not a control.
 *
 * No dropdown primitive exists in @afrilink/ui, so this renders flat buttons
 * rather than inventing one (same call the DNS-export ticket made, per
 * CLAUDE.md 4.2).
 *
 * Clipboard access is blocked outright in some mobile browsers and in any
 * non-secure context, so every action degrades to a selectable input rather
 * than failing silently. That fallback is the pattern already used on
 * SalesAddCustomerPage.
 */

type CopyKind = 'link' | 'whatsapp' | 'email' | 'autopay';

export interface CopyPayLinkProps extends PayLinkMessageInput {
  /**
   * Optional second link that also enrols PayFast Recurring. Pass ONLY for
   * hosting renewals with no live subscription - offering it on a Studio
   * instalment or a domain renewal would enrol the customer in the wrong
   * recurring charge.
   */
  autopayUrl?: string | null;
  /** Matches @afrilink/ui Button's own size union - do not invent values. */
  size?: 'sm' | 'default';
  /** Compact mode for table rows: link + WhatsApp only. */
  dense?: boolean;
}

export function CopyPayLink({ autopayUrl, size = 'sm', dense = false, ...msg }: CopyPayLinkProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState<CopyKind | null>(null);
  const [fallback, setFallback] = useState<string | null>(null);

  async function copy(kind: CopyKind, text: string, label: string) {
    try {
      if (!navigator.clipboard) throw new Error('clipboard_unavailable');
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setFallback(null);
      toast({ tone: 'success', title: 'Copied', body: `${label} copied to clipboard.` });
      window.setTimeout(() => setCopied((c) => (c === kind ? null : c)), 2000);
    } catch {
      // Do not toast an error and leave the user stuck - give them the text.
      setFallback(text);
      toast({
        tone: 'warning',
        title: 'Clipboard blocked',
        body: 'Your browser blocked clipboard access. Select the text below and copy it manually.',
      });
    }
  }

  function icon(kind: CopyKind, Fallback: typeof Link2) {
    return copied === kind ? <Check className="h-4 w-4" /> : <Fallback className="h-4 w-4" />;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-1">
        <Button
          size={size}
          variant="ghost"
          onClick={() => copy('link', msg.payUrl, 'Payment link')}
          title="Copy just the payment URL"
          aria-label={`Copy payment link for ${msg.reference}`}
        >
          {icon('link', Link2)} Link
        </Button>

        <Button
          size={size}
          variant="ghost"
          onClick={() => copy('whatsapp', buildWhatsAppMessage(msg), 'WhatsApp message')}
          title="Copy a formal payment-request message ready to paste into WhatsApp"
          aria-label={`Copy WhatsApp payment message for ${msg.reference}`}
        >
          {icon('whatsapp', MessageCircle)} WhatsApp
        </Button>

        {!dense && (
          <Button
            size={size}
            variant="ghost"
            onClick={() => copy('email', buildEmailBlock(msg), 'Email subject and body')}
            title="Copy a subject line and body ready to paste into an email"
            aria-label={`Copy email payment message for ${msg.reference}`}
          >
            {icon('email', Mail)} Email
          </Button>
        )}

        {autopayUrl && (
          <Button
            size={size}
            variant="ghost"
            onClick={() =>
              copy(
                'autopay',
                buildWhatsAppMessage({ ...msg, payUrl: autopayUrl, autopay: true }),
                'Auto-pay message'
              )
            }
            title="Copy a message whose link settles this invoice AND starts automatic monthly payment. The message tells the customer about the recurring mandate."
            aria-label={`Copy auto-pay message for ${msg.reference}`}
            className="text-success"
          >
            {icon('autopay', RefreshCw)} Auto-pay
          </Button>
        )}
      </div>

      {fallback !== null && (
        <div className="flex flex-col gap-1">
          <Input
            readOnly
            value={fallback}
            onFocus={(e) => e.currentTarget.select()}
            aria-label="Payment message - select and copy manually"
            className="font-mono text-xs"
          />
          <span className="text-xs text-muted-foreground">
            Tap the box to select all, then copy.
          </span>
        </div>
      )}
    </div>
  );
}
